
import JigTextField from '@/components/input/JigTextField.vue'
import { CategoryState, LibraryCategory } from '@/store/modules/teamlibrary/types'
import IconClose from '@svg/IconClose.svg'
import IconInfoFilled from '@svg/IconInfoFilled.svg'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

/**
 * Dialog component for creating or editing a library category.
 *
 * This modal allows users to input or modify category details including the category name,
 * state (e.g., Active, Inactive), and display order. It adapts its title based on whether
 * a new category is being created or an existing one is being edited.
 *
 * The dialog validates that a category name is provided before allowing submission.
 */
@Component({
  components: {
    IconClose,
    IconInfoFilled,
    JigTextField,
  },
})
export default class CategoryDialog extends Vue {
  @Prop({ type: Boolean, required: true }) public value!: boolean
  @Prop({ type: Object, default: null }) public category!: LibraryCategory | null
  @Prop({ type: Array, required: true }) public categoryStates!: CategoryState[]
  @Prop({ type: Object, default: null }) public defaultCategoryState!: CategoryState | null
  @Prop({ type: Boolean, default: false }) public showStaffFields!: boolean
  @Prop({ type: Boolean, default: false }) public isSubmitting!: boolean
  @Prop({ type: Number, default: 0 }) public defaultCategoryOrder!: number

  private isValid: boolean = false
  private localCategoryName: string = ''
  private localCategoryOrder: number = 0
  private localCategoryState: CategoryState | null = null

  private get isVisible(): boolean {
    return this.value
  }

  private set isVisible(val: boolean) {
    this.$emit('input', val)
  }

  private get dialogTitle(): string {
    return this.category != null ? `Category: ${this.category.DisplayName}` : 'New category'
  }

  @Watch('value', { immediate: true })
  private onValueChanged(newVal: boolean) {
    if (newVal) {
      // Dialog opened - initialize with category data or defaults
      this.initializeFormData()
    } else {
      // Dialog closed - reset form
      this.resetFormData()
    }
  }

  @Watch('category')
  private onCategoryChanged() {
    if (this.value) {
      this.initializeFormData()
    }
  }

  private initializeFormData() {
    if (this.category) {
      // Editing existing category
      this.localCategoryName = this.category.DisplayName
      this.localCategoryOrder = this.category.CategoryOrder
      this.localCategoryState = {
        StateId: this.category.StateId,
        StateName: this.category.StateName,
      }
    } else {
      // Creating new category
      this.localCategoryName = ''
      this.localCategoryOrder = this.defaultCategoryOrder
      this.localCategoryState = this.defaultCategoryState ? { ...this.defaultCategoryState } : { StateId: 10, StateName: 'Active' }
    }
  }

  private resetFormData() {
    this.localCategoryName = ''
    this.localCategoryOrder = 0
    this.localCategoryState = this.defaultCategoryState ? { ...this.defaultCategoryState } : null
    this.isValid = false
  }

  private onCancel() {
    this.isVisible = false
    this.$emit('cancel')
  }

  private onSubmit() {
    const categoryData = {
      DisplayName: this.localCategoryName,
      CategoryOrder: Number(this.localCategoryOrder),
      StateId: (this.localCategoryState as CategoryState).StateId,
      StateName: (this.localCategoryState as CategoryState).StateName,
    }

    this.$emit('submit', categoryData)
  }
}
